import { useEffect, useState } from 'react';
import {
  Calendar,
  Plus,
  Target,
  Package,
  CheckSquare,
  Square,
  Sparkles,
  X,
  Loader2,
  ChevronRight,
  Trash2
} from 'lucide-react';
import dayjs from 'dayjs';
import { api } from '@/lib/api';
import { formatCurrency, pad2 } from '@/lib/utils';
import type { PlanningEntity } from '@shared/types';

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  planning: { label: '策划中', cls: 'border-primary text-primary' },
  preparing: { label: '筹备中', cls: 'border-info text-info' },
  live: { label: '直播中', cls: 'border-destructive text-destructive animate-pulse' },
  completed: { label: '已完成', cls: 'border-muted-foreground text-muted-foreground' }
};

const TEMPLATES = [
  { id: 'new-product', name: '新品发布', desc: '核心爆款首发,强调卖点+限时首发价' },
  { id: 'brand-day', name: '品牌日', desc: '全天多时段直播,品牌福利+粉丝答谢' },
  { id: 'daily', name: '日常直播', desc: '高频次日常场,自然流量+私域沉淀' }
];

export default function PlanningPage() {
  const [plannings, setPlannings] = useState<PlanningEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = () => {
    setLoading(true);
    api.getPlannings().then(setPlannings).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleCreate = async (data: Partial<PlanningEntity>) => {
    await api.createPlanning(data);
    setShowModal(false);
    load();
  };

  return (
    <div>
      {/* Header */}
      <header className="border-b-2 border-border pb-8 mb-8">
        <div className="text-xs uppercase tracking-widest text-primary font-space mb-2">
          PLANNING / {dayjs().format('YYYY.MM.DD')}
        </div>
        <h1 className="text-[clamp(3rem,12vw,8rem)] font-bold leading-[0.85] tracking-tighter uppercase font-space text-foreground">
          直播<br />策划<span className="text-primary">.</span>
        </h1>
      </header>

      {/* 操作栏 */}
      <div className="flex items-center justify-between mb-8 border-b-2 border-border pb-4">
        <div className="text-sm text-muted-foreground uppercase tracking-widest">
          共 <span className="text-foreground font-bold">{pad2(plannings.length)}</span> 个策划
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-primary text-black px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-white transition-colors duration-300"
        >
          <Plus className="w-4 h-4" strokeWidth={3} />
          创建策划
        </button>
      </div>

      {/* 策划列表 */}
      {loading ? (
        <div className="text-foreground font-space uppercase tracking-widest text-sm py-32 text-center">
          LOADING<span className="text-primary">_</span>
        </div>
      ) : plannings.length === 0 ? (
        <div className="border-2 border-border p-16 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <div className="text-muted-foreground uppercase tracking-widest">暂无策划</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {plannings.map((p) => (
            <PlanningCard
              key={p.id}
              planning={p}
              onDelete={async () => {
                if (confirm(`确定删除「${p.name}」?`)) {
                  await api.deletePlanning(p.id);
                  load();
                }
              }}
            />
          ))}
        </div>
      )}

      {showModal && <CreatePlanningModal onClose={() => setShowModal(false)} onSubmit={handleCreate} />}
    </div>
  );
}

function PlanningCard({ planning, onDelete }: { planning: PlanningEntity; onDelete: () => void }) {
  const status = STATUS_LABEL[planning.status] ?? STATUS_LABEL.planning;
  const completedTasks = planning.tasks.filter((t) => t.completed).length;

  return (
    <div className="border-2 border-border bg-background p-6 md:p-8 group hover:border-primary transition-colors duration-300">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-2xl font-bold uppercase tracking-tight font-space">{planning.name}</h3>
          <div className="text-sm text-muted-foreground mt-1">
            {planning.hostName} · {planning.date} {planning.time}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 border-2 ${status.cls}`}>
            {status.label}
          </span>
          <button
            onClick={onDelete}
            className="text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {planning.description && (
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{planning.description}</p>
      )}

      {/* 关键数据 */}
      <div className="grid grid-cols-3 gap-0 border-2 border-border mb-4">
        <div className="p-3 border-r-2 border-border">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
            <Target className="w-3 h-3" /> 目标GMV
          </div>
          <div className="font-bold font-space tabular-nums">{formatCurrency(planning.targetGmv)}</div>
        </div>
        <div className="p-3 border-r-2 border-border">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
            <Package className="w-3 h-3" /> 商品数
          </div>
          <div className="font-bold font-space tabular-nums">{pad2(planning.productCount)}</div>
        </div>
        <div className="p-3">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">任务进度</div>
          <div className="font-bold font-space tabular-nums">{planning.taskProgress}%</div>
        </div>
      </div>

      {/* 进度条 */}
      <div className="h-2 border-2 border-border mb-4 overflow-hidden">
        <div
          className="h-full bg-primary"
          style={{ width: `${planning.taskProgress}%` }}
        />
      </div>

      {/* 任务清单 */}
      <div className="border-t-2 border-border pt-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          准备清单 · {completedTasks}/{planning.tasks.length}
        </div>
        <div className="space-y-1">
          {planning.tasks.slice(0, 4).map((task, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {task.completed ? (
                <CheckSquare className="w-4 h-4 text-primary" />
              ) : (
                <Square className="w-4 h-4 text-muted-foreground" />
              )}
              <span className={task.completed ? 'line-through text-muted-foreground' : ''}>{task.name}</span>
            </div>
          ))}
          {planning.tasks.length > 4 && (
            <div className="text-xs text-muted-foreground">+{planning.tasks.length - 4} 项</div>
          )}
        </div>
      </div>

      {/* AI 建议 (仅策划中状态显示) */}
      {planning.aiSuggestions && planning.aiSuggestions.length > 0 && planning.status === 'planning' && (
        <div className="mt-4 border-2 border-primary bg-primary text-black p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">AI 建议</span>
          </div>
          <ol className="space-y-1 text-sm">
            {planning.aiSuggestions.slice(0, 3).map((s, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-space font-bold opacity-60">{pad2(i + 1)}</span>
                <span>{s.title}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function CreatePlanningModal({
  onClose,
  onSubmit
}: {
  onClose: () => void;
  onSubmit: (data: Partial<PlanningEntity>) => Promise<void>;
}) {
  const [template, setTemplate] = useState('daily');
  const [name, setName] = useState('');
  const [hostName, setHostName] = useState('');
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [time, setTime] = useState('20:00');
  const [targetGmv, setTargetGmv] = useState(500000);
  const [productCount, setProductCount] = useState(8);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert('请填写活动名称');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        name,
        template,
        hostName: hostName || '未指定主播',
        date,
        time,
        targetGmv,
        productCount,
        description
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-background border-2 border-border w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b-2 border-border p-6">
          <h2 className="text-2xl font-bold uppercase font-space tracking-tight">创建策划</h2>
          <button onClick={onClose} className="hover:text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          {/* 模板选择 */}
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">
              直播模板
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-border">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTemplate(t.id)}
                  className={`p-4 border-r-2 last:border-r-0 border-border text-left transition-colors duration-200 ${
                    template === t.id ? 'bg-primary text-black' : 'hover:bg-accent'
                  }`}
                >
                  <div className="font-bold uppercase tracking-tight">{t.name}</div>
                  <div className={`text-xs mt-1 ${template === t.id ? 'text-black/80' : 'text-muted-foreground'}`}>
                    {t.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 基本信息 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                活动名称 *
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例:618 大促品牌日"
                className="w-full bg-input border-2 border-border px-4 py-3 outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                主播
              </label>
              <input
                value={hostName}
                onChange={(e) => setHostName(e.target.value)}
                placeholder="例:林清晚"
                className="w-full bg-input border-2 border-border px-4 py-3 outline-none focus:border-primary"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                  日期
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full bg-input border-2 border-border px-3 py-3 outline-none focus:border-primary font-space"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                  时间
                </label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full bg-input border-2 border-border px-3 py-3 outline-none focus:border-primary font-space"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                目标 GMV (元)
              </label>
              <input
                type="number"
                value={targetGmv}
                onChange={(e) => setTargetGmv(Number(e.target.value))}
                className="w-full bg-input border-2 border-border px-4 py-3 outline-none focus:border-primary font-space tabular-nums"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                商品数量
              </label>
              <input
                type="number"
                value={productCount}
                onChange={(e) => setProductCount(Number(e.target.value))}
                className="w-full bg-input border-2 border-border px-4 py-3 outline-none focus:border-primary font-space tabular-nums"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
              活动描述
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="补充活动说明..."
              className="w-full bg-input border-2 border-border px-4 py-3 outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="border-t-2 border-border p-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="border-2 border-border text-foreground hover:bg-accent px-6 py-3 font-bold uppercase tracking-widest text-sm"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-primary text-black hover:bg-white px-6 py-3 font-bold uppercase tracking-widest text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            创建策划
          </button>
        </div>
      </div>
    </div>
  );
}
